'use client';
import { StackedBarChart } from '@mnd-frontend/charts';
import { BrandReportGroupStatisticsBy } from '@mnd-frontend/graphql-schema';
import { DANGER_500, SUCCESS_500 } from '@mnd-frontend/ui/shared/tokens';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import type { Report2PerceptionSentimentOverTimeFragment } from '../../../../generated/types';
import {
  useReportGraphHelpers,
  useReportStaticData,
  useReportTracking,
  useReportTranslation,
} from '../../hooks';
import {
  GraphCaption,
  type HitsModalDetails,
  HitsModalSentiment,
  SkeletonBarChart,
} from '../common';
import { GraphContainer } from '../common/GraphContainer';
import { ScrollContainer } from '../common/ScrollContainer';

export const SentimentAnalysisGraph = ({
  loading,
  sentimentAnalysis,
  groupBy,
}: {
  loading: boolean;
  sentimentAnalysis: Report2PerceptionSentimentOverTimeFragment['sentimentAnalysis'];
  groupBy: Report2PerceptionSentimentOverTimeFragment['groupBy'];
}) => {
  const { fromDate, toDate } = useReportStaticData();

  const { t } = useReportTranslation();
  const { getPeriodLabel, getPeriodLabelLong, getSentimentLegendCaption } = useReportGraphHelpers();
  const { track } = useReportTracking();
  const [details, setDetails] = useState<HitsModalDetails | null>(null);
  const onBarClick = useCallback(
    (clickedDetails: HitsModalDetails) => {
      track({
        event: 'Brand Reports: Clicked Sentiment Over Time graph',
      });
      setDetails(clickedDetails);
    },
    [track],
  );
  const onModalClose = useCallback(() => setDetails(null), []);

  const { data, mappings } = useMemo(() => {
    const mappings: {
      name: string | ReactNode;
      dataKey: 'positive' | 'negative';
      detailsDataKey: 'details' | 'detailsNegative';
      color: string;
    }[] = [
      {
        name: getSentimentLegendCaption({ fromDate, toDate, sentimentType: 'positive' }),
        dataKey: 'positive',
        detailsDataKey: 'details',
        color: SUCCESS_500,
      },
      {
        name: getSentimentLegendCaption({ fromDate, toDate, sentimentType: 'negative' }),
        detailsDataKey: 'detailsNegative',
        dataKey: 'negative',
        color: DANGER_500,
      },
    ];

    return {
      data: sentimentAnalysis.map(sentiment => {
        const year = new Date(sentiment.startAt).getFullYear();
        return {
          name: getPeriodLabel(groupBy, year, sentiment.month, sentiment.week),

          positive: sentiment.positive,
          negative: sentiment.negative,
          month: sentiment.month,
          details: {
            startAt: sentiment.startAt,
            endAt: sentiment.endAt,
            label: getPeriodLabelLong(groupBy, year, sentiment.month, sentiment.week),
          },
          detailsNegative: {
            startAt: sentiment.startAt,
            endAt: sentiment.endAt,
            label: getPeriodLabelLong(groupBy, year, sentiment.month, sentiment.week),
          },
        };
      }),
      mappings,
    };
  }, [
    getSentimentLegendCaption,
    fromDate,
    toDate,
    sentimentAnalysis,
    getPeriodLabel,
    groupBy,
    getPeriodLabelLong,
  ]);
  const showMonths = groupBy === BrandReportGroupStatisticsBy.Month;

  return (
    <ScrollContainer minWidth="35rem">
      <GraphContainer data-testid="sentiment-analysis-graph">
        {!loading && (
          <>
            <GraphCaption>{t('reports_sentiment')}</GraphCaption>
            <StackedBarChart
              height={450}
              data={data}
              mappings={mappings}
              showMonths={showMonths}
              onBarClick={onBarClick}
            />
          </>
        )}
        {loading && <SkeletonBarChart />}
        {details ? <HitsModalSentiment onClose={onModalClose} details={details} /> : null}
      </GraphContainer>
    </ScrollContainer>
  );
};
